'use client'
import React, { useEffect, useRef, useState } from 'react'
import NextLink from 'next/link'
import { Link as AscendLink, Typography } from '@achieve/ascend'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { ROUTE_EXCEPT_NOFOLLOW, ROUTE_ASSET_EXCEPT } from 'constants/navigation'
import { getProductAndAddDefaultUtmsToLink } from 'utils/queryManipulation'
import Cookies from 'js-cookie'
import { disableClientSideRoutes } from 'constants/experiments'
import { preserveQueryParams, sanitizeUrlParams, moveAnchorRight } from 'utils/shared'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import logger from '@achieve/cx-logger'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'

const LinkWrapper = React.forwardRef(({ children, refAs, ...props }, ref) => {
  if (refAs) {
    props[refAs] = ref
  }

  return (
    <>
      {React.isValidElement(children)
        ? React.cloneElement(children, props)
        : typeof children === 'string'
        ? children
        : null}
    </>
  )
})

LinkWrapper.displayName = 'LinkWrapper'

/**
 *
 * @param {string} href original href string
 * @param {ReactNode} children child components wrapped with AchieveLink
 * @param {boolean} withNextLink if true use next/link, default use AscendLink
 * @param {RefObject} refAs external ref to be used in the link child
 * @returns {ReactElement} returns link wrapped child component with forwarded
 *                         ref, passed props and transformed/memoized href
 *                         with original query params preserved
 */
const AchieveLink = ({
  href,
  children,
  withNextLink = false,
  refAs,
  track,
  onClick,
  rel,
  noLink,
  noPreserveParams,
  ...restProps
}) => {
  const trackRefApiResponse = useRef()
  const continuePropagation = useRef()
  const { waitForNavigation } = useTriggerEvents()

  const { query } = useAppRouter()

  const [finalHref, setFinalHref] = useState(href)
  const [trackAsConversion] = useState(false)

  const visitId = Cookies.get('eh_visit_id')
  useEffect(() => {
    let calcHref = href
    calcHref = getProductAndAddDefaultUtmsToLink(preserveQueryParams(href, query, visitId))
    if (noPreserveParams) calcHref = href
    calcHref = moveAnchorRight(calcHref)
    setFinalHref(sanitizeUrlParams(calcHref))
  }, [href, noPreserveParams, query, visitId])

  const noFollowExceptionRoute = ROUTE_EXCEPT_NOFOLLOW.findIndex(
    (exception) => String(`${href}`).toLowerCase().includes(exception) >= 0
  )
  if (
    String(`${href}`).toLowerCase().includes('http') &&
    noFollowExceptionRoute == -1 &&
    !String(`${href}`).toLowerCase().includes(ROUTE_ASSET_EXCEPT)
  ) {
    if (!String(`${linkRel}`).toLowerCase().includes('nofollow')) {
      linkRel = `${rel ? `${rel} ` : ''}nofollow`
    }
    restProps = { ...restProps, target: '_blank' }
  }
  if (disableClientSideRoutes.includes(finalHref)) {
    onClick = (e) => {
      e?.preventDefault()
      window.location.href = finalHref
    }
  }
  let linkRel = rel ?? null
  let LinkComponent = withNextLink && !noLink ? NextLink : AscendLink
  const nextProps = withNextLink && !noLink ? { passHref: true } : {}

  const handleClick = (alreadyTracked) => {
    if (track && !alreadyTracked) {
      trackRefApiResponse.current.click()
    }

    if (onClick) {
      onClick()
    }
  }

  let linkNoCrawl = false
  if (typeof finalHref === 'string' && finalHref?.substring(0, 15).includes('legal')) {
    linkNoCrawl = true
    LinkComponent = Typography
  }

  let component
  if (linkNoCrawl) {
    component = 'div'
  } else if (finalHref && !noLink) {
    component = 'a'
  } else {
    component = 'span'
  }

  let extraProps = {}
  if (noLink || linkNoCrawl) {
    extraProps = {}
  } else {
    extraProps = {
      href: finalHref,
    }
  }

  return (
    <>
      <LinkComponent
        component={component}
        {...extraProps}
        rel={linkRel}
        ref={continuePropagation}
        underline="hover"
        onClick={async (e) => {
          if (finalHref && finalHref.indexOf('http') !== -1 && !linkNoCrawl) {
            if (e.nativeEvent.isTrusted) {
              e.preventDefault()
              e.stopPropagation()
              await waitForNavigation({
                ...track,
              })
                .catch(() => {
                  logger.log('Event timed out')
                  logger.log({ track })
                })
                .finally(() => {
                  continuePropagation.current.click()
                })
            } else {
              handleClick(true)
            }
          } else if (linkNoCrawl) {
            window.open(finalHref, '_self')
          } else {
            handleClick()
          }
        }}
        {...restProps}
        {...nextProps}
      >
        {children?.type === 'a' ? children : <LinkWrapper refAs={refAs}>{children}</LinkWrapper>}
      </LinkComponent>
      <TrackingWrapper
        track={{ href: finalHref, ...track }}
        onClick={onClick}
        withNextLink={withNextLink}
        trackAsConversion={trackAsConversion}
        className="!hidden"
      >
        <span ref={trackRefApiResponse}></span>
      </TrackingWrapper>
    </>
  )
}

export { AchieveLink }
